import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Box, Text } from '@ltvco/refresh-lib/theme';
import { useState, useEffect } from 'react';

import {
  InlineLoading,
  ErrorStateContainer,
  EmptyStateContainer,
  InfoDisclaimerBox,
  PaginationControls,
  PaginationOverlay,
  ResultsContainer,
} from '../../../shared';
import { useBaseCourtApi } from '../../../shared/hooks';
import { type OpinionsResponse } from '../../../types';
import { OpinionsResults } from '../../OpinionsResults';

interface SimilarCasesProps {
  caseName: string;
  similarCases: OpinionsResponse | null;
  error?: string;
  isLoading?: boolean;
}

export const SimilarCases = ({
  caseName,
  similarCases,
  error,
  isLoading = false,
}: SimilarCasesProps) => {
  const { links } = useAppConstantsContext();
  const { fetchPaginationPage } = useBaseCourtApi();
  const [currentResults, setCurrentResults] = useState<OpinionsResponse | null>(
    similarCases
  );
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  useEffect(() => {
    setCurrentResults(similarCases);
  }, [similarCases]);

  // Simple case name query for View All - no related: keyword
  const searchQuery = caseName.trim() ? `"${caseName.trim()}"` : '';

  const handleViewAll = () => {
    if (!searchQuery) return;
    window.open(
      `${
        links.baseUrl
      }/dashboard/court-cases/opinions?query=${encodeURIComponent(
        searchQuery
      )}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handlePreviousPage = async () => {
    if (!currentResults?.previous) {
      return;
    }

    try {
      setIsPaginationLoading(true);
      const newResults = await fetchPaginationPage<OpinionsResponse>(
        currentResults.previous,
        'search'
      );
      setCurrentResults(newResults);
    } catch (error) {
      console.error('Error fetching previous page', error);
    } finally {
      setIsPaginationLoading(false);
    }
  };

  const handleNextPage = async () => {
    if (!currentResults?.next) {
      return;
    }

    try {
      setIsPaginationLoading(true);
      const newResults = await fetchPaginationPage<OpinionsResponse>(
        currentResults.next,
        'search'
      );
      setCurrentResults(newResults);
    } catch (error) {
      console.error('Error fetching next page', error);
    } finally {
      setIsPaginationLoading(false);
    }
  };

  if (isLoading) {
    return <InlineLoading />;
  }

  if (error) {
    return (
      <ErrorStateContainer>
        <Text color="error">Error loading similar cases</Text>
      </ErrorStateContainer>
    );
  }

  if (!currentResults || currentResults.results.length === 0) {
    return (
      <EmptyStateContainer>
        <Text variant="h2" color="text.secondary">
          No similar cases found
        </Text>
      </EmptyStateContainer>
    );
  }

  return (
    <ResultsContainer>
      <InfoDisclaimerBox>
        <Text variant="body2" color="text.primary">
          The Similar Cases query helps you explore precedents or comparable
          rulings. It analyzes textual similarities to a given case to build a
          list of similar cases, their names, links and filing dates.
        </Text>
      </InfoDisclaimerBox>
      <Box sx={{ position: 'relative' }}>
        <OpinionsResults results={currentResults.results} />
        <PaginationControls
          hasNext={!!currentResults.next}
          hasPrevious={!!currentResults.previous}
          isLoading={isPaginationLoading}
          onNext={handleNextPage}
          onPrevious={handlePreviousPage}
          showViewAll={!!searchQuery}
          onViewAll={handleViewAll}
        />
        <PaginationOverlay isVisible={isPaginationLoading} />
      </Box>
    </ResultsContainer>
  );
};
